import Api from "./api";
import {
  fetchTenantData,
  updateFavicon,
  updatePageTitle,
  applyTenantStyles,
} from "./whitelabelService";

/**
 * Serviço para operações de tenant e temas
 * Integração com os endpoints reais do TenantController
 */
class TenantService {
  /**
   * Buscar tenant por ID específico
   * @param {string} tenantId - ID do tenant
   * @returns {Promise} Dados do tenant
   */
  static async getById(tenantId) {
    try {
      const response = await Api.get(`/tenant/getById/${tenantId}`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar tenant por ID:", error);
      throw error;
    }
  }

  static async getThemeByIdentifier(tenantIdentifier) {
    try {
      const response = await Api.get(`api/tenant/${tenantIdentifier}/theme`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar tema por identificador:", error);
      throw error;
    }
  }

  static async getByIdSimplified(tenantId) {
    try {
      const response = await Api.get(`/tenant/getByIdSimplified/${tenantId}`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar tenant por ID:", error);
      throw error;
    }
  }

  static async getByIdBackOffice(tenantId) {
    try {
      const response = await Api.get(`/tenant/getByIdBackoffice/${tenantId}`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar tenant por ID:", error);
      throw error;
    }
  }

  static async getSyncsByTenantId(tenantId) {
    try {
      const response = await Api.get(`/tenant/getSyncsByTenant/${tenantId}`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar tenant por ID:", error);
      throw error;
    }
  }

  static async requestIncrementalLoad(integrationId, erp) {
    try {
      const response = await Api.post("/sync/incrementalLoad", {
        integrationId,
        erp,
      });
      return response;
    } catch (error) {
      console.error("❌ Erro ao solicitar carga incremental:", error);
      throw error;
    }
  }
  /**
   * Buscar todos os tenants com filtros
   * @param {Object} filters - Filtros para busca
   * @returns {Promise} Lista de tenants
   */
  static async getAll(filters = {}) {
    try {
      const requestData = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 10,
        ...filters,
      };

      const response = await Api.post("/tenant/getAll", requestData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar lista de tenants:", error);
      throw error;
    }
  }

  static async getStats() {
    try {
      const response = await Api.post("/tenant/getStats");
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar stats de tenants:", error);
      throw error;
    }
  }

  static async getAllExecutivos(filters = {}) {
    try {
      const requestData = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 10,
        ...filters,
      };

      const response = await Api.post("/executivos/getAll", requestData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar lista de tenants:", error);
      throw error;
    }
  }

  static async getAllPlanos(filters = {}) {
    try {
      const requestData = {
        page: filters.page || 1,
        pageSize: filters.pageSize || 10,
        sortOrder: filters.sortOrder || "desc",
        sorterField: filters.sorterField || "updated",
        ...filters,
      };

      const response = await Api.post("/planos/getAll", requestData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar lista de planos:", error);
      throw error;
    }
  }

  /**
   * Buscar tema do tenant (usando getById para acessar o Theme)
   * @param {string} tenantId - ID do tenant
   * @returns {Promise} Tema do tenant
   */
  static async getTheme(tenantId = null) {
    try {
      if (!tenantId) {
        // Se não informar ID, retornar tema vazio para usar defaults
        return {
          data: {
            response: null,
            success: true,
          },
        };
      }

      const response = await this.getByIdSimplified(tenantId);

      if (response?.data?.response) {
        // Retornar dados completos do tenant, não apenas o theme
        return {
          data: {
            response: response.data.response,
            success: true,
          },
        };
      }

      // Se não tem tema, retornar null para usar defaults
      return {
        data: {
          response: null,
          success: true,
        },
      };
    } catch (error) {
      console.error("❌ Erro ao buscar tema do tenant:", error);
      throw error;
    }
  }

  /**
   * Criar ou atualizar um tenant (incluindo tema)
   * @param {Object} tenantData - Dados do tenant
   * @returns {Promise} Tenant salvo
   */
  static async upsert(tenantData) {
    try {
      const response = await Api.post("/tenant/upsert", tenantData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao salvar tenant:", error);
      throw error;
    }
  }

  static async upsertSimplified(tenantData) {
    try {
      const response = await Api.post("/tenant/upsertSimplified", tenantData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao salvar tenant:", error);
      throw error;
    }
  }

  static async upsertExecutivo(executivoData) {
    try {
      const response = await Api.post("/executivos/upsert", executivoData);
      return response;
    } catch (error) {
      console.error("❌ Erro ao salvar executivo:", error);
      throw error;
    }
  }

  /**
   * Salvar tema do tenant (usando upsert)
   * @param {Object} themeData - Dados do tema
   * @param {string} tenantId - ID do tenant (opcional)
   * @returns {Promise} Tema salvo
   */
  static async saveTheme(themeData, tenantId = null) {
    try {
      const formattedTheme = this.formatThemeForBackend(themeData);

      let tenantPayload;

      if (tenantId && tenantId !== "00000000-0000-0000-0000-000000000000") {
        // Busca só os dados básicos do tenant (o getById completo traz syncs/relatórios/cobranças
        // e é lento). Se não vier, falha: nunca criar outro tenant a partir desta tela.
        const existingTenantResponse = await this.getByIdSimplified(tenantId);
        const existingTenant = existingTenantResponse?.data?.response;
        if (!existingTenant?.id) {
          throw new Error("Tenant não encontrado para salvar o tema");
        }

        // Fazer merge dos dados existentes com o novo tema
        tenantPayload = {
          id: existingTenant.id,
          nome: themeData.companyName || existingTenant.nome,
          identificador: existingTenant.identificador,
          ativo: existingTenant.ativo,
          franquiaId: existingTenant.franquiaId,
          razaoSocial: existingTenant.razaoSocial,
          cnpj: existingTenant.cnpj,
          email: existingTenant.email,
          telefone: existingTenant.telefone,
          theme: formattedTheme, // Apenas atualizar o tema
        };
      } else {
        // Criar novo tenant
        tenantPayload = {
          id: "00000000-0000-0000-0000-000000000000", // Guid vazio para criar
          nome: themeData.companyName || "Minha Empresa",
          identificador: `tenant-${Date.now()}`,
          ativo: true,
          theme: formattedTheme,
        };
      }

      const response = await this.upsertSimplified(tenantPayload);
      return response;
    } catch (error) {
      console.error("❌ Erro ao salvar tema do tenant:", error);
      throw error;
    }
  }

  /**
   * Upload de arquivo do tenant
   * @param {File} file - Arquivo para upload
   * @param {string} tenantId - ID do tenant (opcional)
   * @returns {Promise} URL do arquivo enviado
   */
  static async uploadFile(file, tenantId = null) {
    try {
      const formData = new FormData();
      formData.append("Files", file); // Usar 'Files' conforme FileUploadRequest

      if (tenantId) {
        formData.append("IdTenant", tenantId);
      }

      const response = await Api.post("/tenant/uploadFile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response;
    } catch (error) {
      console.error("❌ Erro ao fazer upload do arquivo:", error);
      throw error;
    }
  }

  /**
   * Upload de logo do tenant
   * @param {File} file - Arquivo do logo
   * @param {string} tenantId - ID do tenant (opcional)
   * @returns {Promise} URL do logo enviado
   */
  static async uploadLogo(file, tenantId = null) {
    return this.uploadFile(file, tenantId);
  }

  /**
   * Buscar arquivo do tenant
   * @param {string} fileName - Nome do arquivo
   * @returns {Promise} Arquivo
   */
  static async getFile(fileName) {
    try {
      const response = await Api.get(`/tenant/getFile/${fileName}`, {
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("❌ Erro ao buscar arquivo:", error);
      throw error;
    }
  }

  /**
   * Remover arquivo do tenant
   * @param {string} fileName - Nome do arquivo
   * @param {string} tenantId - ID do tenant
   * @returns {Promise} Resultado da operação
   */
  static async removeFile(fileName, tenantId) {
    try {
      const fileDto = {
        files: fileName,
        idTenant: tenantId,
      };

      const response = await Api.post("/tenant/removeFile", fileDto);
      return response;
    } catch (error) {
      console.error("❌ Erro ao remover arquivo:", error);
      throw error;
    }
  }

  /**
   * Deletar tenant
   * @param {string} tenantId - ID do tenant
   * @returns {Promise} Resultado da operação
   */
  static async delete(tenantId) {
    try {
      const response = await Api.delete(`/tenant/${tenantId}`);
      return response;
    } catch (error) {
      console.error("❌ Erro ao deletar tenant:", error);
      throw error;
    }
  }

  /**
   * Formatar dados do tema para o backend
   * @param {Object} themeData - Dados do tema do frontend
   * @returns {Object} Dados formatados para o backend
   */
  static formatThemeForBackend(themeData) {
    const formattedTheme = {
      logo: themeData.companyLogo || "",
      favIcon: themeData.companyLogo || "", // Usar mesmo logo como favicon
      backgroundImage: "",
      colors: {
        primary: themeData.primaryColor || "#7C3AED",
        secondary: themeData.secondaryColor || "#52c41a",
        gradient1: themeData.primaryColor || "#7C3AED",
        gradient2: themeData.secondaryColor || "#52c41a",
      },
      customStyles: {
        sidebarBackground: "",
        headerBackground: "",
        buttonPrimary: themeData.primaryColor || "#7C3AED",
        buttonSecondary: themeData.secondaryColor || "#52c41a",
      },
    };

    console.groupEnd();

    return formattedTheme;
  }

  /**
   * Mapear dados completos do tenant do backend para o frontend
   * @param {Object} backendData - Dados completos do tenant vindo do backend (com tenant.theme)
   * @returns {Object} Dados formatados para o frontend
   */
  static mapThemeToFrontend(backendData) {
    if (!backendData) {
      const defaultTheme = {
        companyLogo: "",
        primaryColor: "#7C3AED",
        secondaryColor: "#52c41a",
        tenantName: "Tenant Padrão",
        tenantId: null,
        tenantIdentifier: null,
      };
      console.groupEnd();
      return defaultTheme;
    }

    // Extrair dados do tema se existir
    const theme = backendData.theme || {};

    const mappedTheme = {
      companyLogo: theme.logo || "",
      primaryColor: theme.colors?.primary || "#7C3AED",
      secondaryColor: theme.colors?.secondary || "#52c41a",
      tenantName: backendData.nome || backendData.name || "Tenant Padrão",
      tenantId: backendData.id,
      tenantIdentifier: backendData.identificador,
    };

    return mappedTheme;
  }

  /**
   * Validar cor hexadecimal
   * @param {string} color - Cor em formato hex
   * @returns {boolean} Se a cor é válida
   */
  static isValidHexColor(color) {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  }

  /**
   * Converter dados do WhitelabelService para formato de tema
   * @param {Object} tenantData - Dados do tenant do WhitelabelService
   * @returns {Object} Tema no formato esperado
   */
  static convertWhitelabelToTheme(tenantData) {
    if (!tenantData) return null;

    return {
      logo: tenantData.logo || "",
      favIcon: tenantData.fav_icon || "",
      backgroundImage: "",
      colors: {
        primary: tenantData.colors?.primary || "#7C3AED",
        secondary: tenantData.colors?.secondary || "#52c41a",
        gradient1:
          tenantData.colors?.gradient1 ||
          tenantData.colors?.primary ||
          "#7C3AED",
        gradient2:
          tenantData.colors?.gradient2 ||
          tenantData.colors?.secondary ||
          "#52c41a",
      },
      customStyles: {
        sidebarBackground: "",
        headerBackground: "",
        buttonPrimary: tenantData.colors?.primary || "#7C3AED",
        buttonSecondary: tenantData.colors?.secondary || "#52c41a",
      },
    };
  }

  /**
   * Buscar lista de tenants disponíveis (para demo/whitelabel)
   * @returns {Array} Lista de tenants
   */
  static async getAvailableTenants() {
    try {
      // Usar getAll para buscar tenants reais
      const response = await this.getAll({ pageSize: 100 });

      if (response?.data?.response?.items) {
        return response.data.response.items.map((tenant) => ({
          id: tenant.id,
          name: tenant.nome,
          identifier: tenant.identificador,
          color: tenant.theme?.colors?.primary || "#7C3AED",
          description: `Tema ${tenant.nome}`,
        }));
      }

      // Fallback para tenants mockados se não houver dados reais
      return [
        {
          id: "default",
          name: "Padrão",
          color: "#7C3AED",
          description: "Tema padrão do sistema",
        },
      ];
    } catch (error) {
      console.error("❌ Erro ao buscar tenants disponíveis:", error);

      // Fallback para tenants mockados
      return [
        {
          id: "default",
          name: "Padrão",
          color: "#7C3AED",
          description: "Tema padrão do sistema",
        },
      ];
    }
  }

  /**
   * Carregar tema de um tenant específico usando WhitelabelService
   * @param {string} tenantId - ID do tenant
   * @returns {Promise} Dados do tema
   */
  static async loadTenantTheme(tenantId) {
    try {
      // Primeiro tentar buscar do backend real
      const response = await this.getTheme(tenantId);

      if (response?.data?.response) {
        return response;
      }

      // Fallback para WhitelabelService
      const tenantData = await fetchTenantData(tenantId);
      if (tenantData) {
        // Aplicar favicon e título
        if (tenantData.fav_icon) {
          updateFavicon(tenantData.fav_icon);
        }
        if (tenantData.name) {
          updatePageTitle(tenantData.name);
        }

        // Aplicar estilos customizados
        applyTenantStyles(tenantData);

        return {
          data: {
            response: this.convertWhitelabelToTheme(tenantData),
            success: true,
          },
        };
      }
      return null;
    } catch (error) {
      console.error("❌ Erro ao carregar tema do tenant:", error);
      throw error;
    }
  }
}

export default TenantService;
